import type { RuntimeEnv } from './types';
import { NodeGLProbe } from '../../webgl';
import { createDominoDocument } from './domino-adapter';

const getGlobalDocument = (): Document | undefined =>
  'document' in globalThis ? (globalThis.document as Document | undefined) : undefined;

const getGlobalWindow = (): (Window & typeof globalThis) | undefined =>
  'window' in globalThis ? (globalThis.window as (Window & typeof globalThis) | undefined) : undefined;

const resolveServerDocument = (): Document => {
  const globalDocument = getGlobalDocument();
  if (globalDocument) {
    return globalDocument;
  }
  return createDominoDocument();
};

const resolveServerWindow = (documentRef: Document): Window & typeof globalThis => {
  const globalWindow = getGlobalWindow();
  if (globalWindow) {
    return globalWindow;
  }

  const view = documentRef.defaultView;
  if (view) {
    return view as Window & typeof globalThis;
  }

  return globalThis as unknown as Window & typeof globalThis;
};

const disposeElement = (element: Element): void => {
  const canvasLikeElement = element as Element & {
    getContext?: (type: '2d') => CanvasRenderingContext2D | null;
    width?: number;
    height?: number;
  };

  if (typeof canvasLikeElement.getContext === 'function') {
    const ctx = canvasLikeElement.getContext('2d');
    const width = typeof canvasLikeElement.width === 'number' ? canvasLikeElement.width : 0;
    const height = typeof canvasLikeElement.height === 'number' ? canvasLikeElement.height : 0;
    ctx?.clearRect(0, 0, width, height);
  }

  if (typeof element.removeAttribute === 'function') {
    element.removeAttribute('src');
  }
};

export const getNodeRuntimeEnv = (): RuntimeEnv => {
  const document = resolveServerDocument();
  const window = resolveServerWindow(document);

  return {
    document,
    window,
    isTouchSupported: false,
    WebGLProbe: new NodeGLProbe(),
    dispose: disposeElement,
  };
};
